/**
 * CoursePage - Individual course view with quiz player.
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { getTelegram, isTelegramMiniApp } from "../../../lib/telegram.ts";
import { Skeleton } from "../../../components/ui/Skeleton.tsx";
import {
  IconAcademicCap,
  IconBookOpen,
  IconExternalLink,
  IconCheck,
  IconTrophy,
} from "../../../components/Icons.tsx";
import { ErrorDisplay } from "../../../components/ErrorDisplay.tsx";
import { useCourse, useExamAttempt, useTrackCourseVisit } from "../hooks";
import { QuizPlayer } from "./QuizPlayer";

// =============================================================================
// LOADING SKELETON
// =============================================================================

function CoursePageSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-48 rounded-2xl" />
      <Skeleton className="h-20 rounded-2xl" />
      <Skeleton className="h-14 rounded-xl" />
      <Skeleton className="h-14 rounded-xl" />
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CoursePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  const { course, isLoading, error, refetch } = useCourse(courseId ?? "");
  const { startExam, isLoading: isStartingExam } = useExamAttempt();
  const { trackVisit } = useTrackCourseVisit();

  const [showQuiz, setShowQuiz] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);

  // Track visit on mount (fire once)
  useEffect(() => {
    if (courseId) {
      trackVisit(courseId);
    }
  }, [courseId]); // trackVisit is stable via useCallback

  // If there's an active attempt with a current question, show quiz automatically
  useEffect(() => {
    if (course?.exam?.currentQuestion && course?.exam?.currentAttempt && !course.exam.currentAttempt.completedAt) {
      setShowQuiz(true);
    }
  }, [course?.exam?.currentQuestion, course?.exam?.currentAttempt]);

  // BackButton (Mini App / PWA)
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (showQuiz) {
        setShowQuiz(false);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/academy");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, showQuiz]);

  const handleStartQuiz = useCallback(async () => {
    if (!course?.exam?.id) return;
    
    setQuizError(null);
    try {
      await startExam(course.exam.id);
      await refetch();
      setShowQuiz(true);
    } catch (err) {
      setQuizError(err instanceof Error ? err.message : "Не удалось начать тест");
    }
  }, [course?.exam?.id, startExam, refetch]);

  const handleQuizComplete = useCallback(() => {
    setShowQuiz(false);
    refetch();
  }, [refetch]);

  const handleOpenCourse = useCallback(() => {
    if (!course?.externalUrl) return;
    
    if (isTelegramMiniApp() && tg?.openLink) {
      tg.openLink(course.externalUrl);
    } else {
      window.open(course.externalUrl, "_blank", "noopener,noreferrer");
    }
  }, [course?.externalUrl, tg]);

  if (error) {
    return (
      <Layout>
        <ErrorDisplay
          error={error.message}
          onRetry={refetch}
          className="mt-6"
        />
      </Layout>
    );
  }

  if (isLoading || !course) {
    return (
      <Layout>
        <CoursePageSkeleton />
      </Layout>
    );
  }

  const isPassed = course.userProgress?.examPassed;
  const hasExam = !!course.exam;
  const questionsCount = course.exam?.questionsCount ?? 0;
  const currentAttempt = course.exam?.currentAttempt;
  const isExamInProgress = currentAttempt && !currentAttempt.completedAt;

  // Show quiz player
  if (showQuiz && course.exam?.currentQuestion) {
    return (
      <Layout>
        <QuizPlayer
          course={course}
          onComplete={handleQuizComplete}
          onExit={() => setShowQuiz(false)}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero section */}
      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50">
        {course.imageUrl && (
          <div className="aspect-video overflow-hidden">
            <img
              src={course.imageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6 xs:p-8">
          <div className="flex items-start gap-3">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isPassed
                  ? "bg-emerald-50/80 text-emerald-600"
                  : "bg-blue-50/80 text-blue-600/75"
              }`}
            >
              {isPassed ? (
                <IconTrophy className="w-6 h-6" />
              ) : (
                <IconAcademicCap className="w-6 h-6" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl xs:text-2xl font-bold text-slate-900">
                {course.title}
              </h1>
              {course.description && (
                <p className="text-sm text-slate-600 mt-2">
                  {course.description}
                </p>
              )}
              {isPassed && (
                <div className="flex items-center gap-2 mt-3 text-sm text-emerald-600 font-medium">
                  <IconCheck className="w-4 h-4" />
                  Тест пройден
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Passed result card */}
      {isPassed && currentAttempt && (
        <div className="mt-4 rounded-2xl overflow-hidden bg-emerald-50/50 shadow-sm border border-emerald-100 ring-1 ring-emerald-200/50 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <IconTrophy className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-semibold text-emerald-900">
                Поздравляем!
              </p>
              <p className="text-sm text-emerald-700">
                Вы успешно прошли контрольные вопросы
                {currentAttempt.totalWrongAttempts > 0 && (
                  <> (попыток: {currentAttempt.totalWrongAttempts + questionsCount})</>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600">
                {currentAttempt.percentage}%
              </p>
              <p className="text-xs text-emerald-600/75">результат</p>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-6 space-y-3">
        {/* External course link - only show if URL exists */}
        {course.externalUrl && (
          <button
            type="button"
            onClick={handleOpenCourse}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 text-left transition-all active:scale-[0.99]"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
              <IconBookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">
                Перейти к материалам курса
              </p>
              <p className="text-xs text-slate-500 truncate">
                {course.externalUrl}
              </p>
            </div>
            <IconExternalLink className="w-5 h-5 text-slate-400 flex-shrink-0" />
          </button>
        )}

        {/* Quiz button */}
        {hasExam && (
          <button
            type="button"
            onClick={handleStartQuiz}
            disabled={isStartingExam}
            className={`w-full flex items-center gap-3 p-4 rounded-xl shadow-sm border text-left transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed ${
              isPassed
                ? "bg-white/95 border-slate-100 ring-1 ring-slate-200/50"
                : isExamInProgress
                  ? "bg-amber-50/80 border-amber-200 ring-1 ring-amber-200/50"
                  : "bg-emerald-50/80 border-emerald-200 ring-1 ring-emerald-200/50"
            }`}
          >
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isPassed
                  ? "bg-slate-100 text-slate-500"
                  : isExamInProgress
                    ? "bg-amber-100 text-amber-600"
                    : "bg-emerald-100 text-emerald-600"
              }`}
            >
              <IconAcademicCap className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold ${
                  isPassed ? "text-slate-700" : "text-slate-900"
                }`}
              >
                {isPassed
                  ? "Пройти ещё раз"
                  : isExamInProgress
                    ? "Продолжить тест"
                    : "Пройти контрольные вопросы"}
              </p>
              <p className="text-xs text-slate-500">
                {questionsCount} {getQuestionsLabel(questionsCount)}
              </p>
            </div>
            {isStartingExam ? (
              <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin flex-shrink-0" />
            ) : (
              <span
                className={`text-sm font-medium flex-shrink-0 ${
                  isPassed ? "text-slate-400" : "text-emerald-600"
                }`}
              >
                {isExamInProgress ? "Продолжить →" : "Начать →"}
              </span>
            )}
          </button>
        )}

        {quizError && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-100">
            <p className="text-sm text-red-600">{quizError}</p>
          </div>
        )}
      </div>

      <div className="h-4" />
    </Layout>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function getQuestionsLabel(count: number): string {
  if (count === 1) return "вопрос";
  if (count >= 2 && count <= 4) return "вопроса";
  return "вопросов";
}
